// Keyboard / mouse state, sampled once per frame by the app.

export class Input {
  private readonly down = new Set<string>();
  private readonly pressedFrame = new Set<string>();
  /** Characters typed this frame (for symbol keys, whose codes move with the keyboard layout). */
  private readonly typedFrame = new Set<string>();
  mouseDX = 0;
  mouseDY = 0;
  wheel = 0;
  dragging = false;
  pointerLocked = false;
  sensitivity = 1;
  /** Where the pointer is (client pixels), once it has moved at all. */
  pointerX = 0;
  pointerY = 0;
  pointerKnown = false;
  /** The pointer moved this frame with no button held (not a drag). */
  hovered = false;
  /** Left button held on the view, and how far the pointer has travelled since (px): a click or a drag. */
  private leftDown = false;
  private travel = 0;
  private readonly canvas: HTMLElement;

  constructor(canvas: HTMLElement) {
    this.canvas = canvas;
    window.addEventListener('keydown', (e) => {
      if (isTyping(e)) return;
      if (!this.down.has(e.code)) this.pressedFrame.add(e.code);
      if (!e.repeat && e.key.length === 1) this.typedFrame.add(e.key);
      this.down.add(e.code);
      if (['Tab', 'Space', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.code)) e.preventDefault();
    });
    window.addEventListener('keyup', (e) => this.down.delete(e.code));
    window.addEventListener('blur', () => this.down.clear());
    canvas.addEventListener('mousedown', (e) => {
      if (e.button === 0 || e.button === 2) this.dragging = true;
      if (e.button === 0) {
        this.leftDown = true;
        this.travel = 0;
      }
      // The middle button is a key like any other (no autoscroll).
      if (e.button === 1) {
        this.pressedFrame.add('MouseMiddle');
        e.preventDefault();
      }
    });
    window.addEventListener('mouseup', (e) => {
      this.dragging = false;
      // A left click is a press that didn't turn into a drag ('MouseLeft').
      if (e.button === 0 && this.leftDown && this.travel < 6) this.pressedFrame.add('MouseLeft');
      if (e.button === 0) this.leftDown = false;
    });
    window.addEventListener('mousemove', (e) => {
      this.pointerX = e.clientX;
      this.pointerY = e.clientY;
      this.pointerKnown = true;
      if (this.leftDown) this.travel += Math.abs(e.movementX) + Math.abs(e.movementY);
      if (!this.dragging) this.hovered = true;
      if (this.dragging || this.pointerLocked) {
        this.mouseDX += e.movementX * this.sensitivity;
        this.mouseDY += e.movementY * this.sensitivity;
      }
    });
    canvas.addEventListener('wheel', (e) => {
      this.wheel += Math.sign(e.deltaY) * Math.min(3, Math.abs(e.deltaY) / 60);
      e.preventDefault();
    }, { passive: false });
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());
    document.addEventListener('pointerlockchange', () => {
      this.pointerLocked = document.pointerLockElement === this.canvas;
    });
  }

  isDown(code: string): boolean {
    return this.down.has(code);
  }

  /** True only on the frame the key went down. */
  pressed(code: string): boolean {
    return this.pressedFrame.has(code);
  }

  /** True on the frame one of these characters was typed ('+', '-', ...), whatever the layout. */
  typed(...chars: string[]): boolean {
    return chars.some((c) => this.typedFrame.has(c));
  }

  axis(neg: string[], pos: string[]): number {
    let v = 0;
    if (neg.some((c) => this.down.has(c))) v -= 1;
    if (pos.some((c) => this.down.has(c))) v += 1;
    return v;
  }

  requestLock(): void {
    // Refused in some frames and embeds; dragging to look still works then.
    if (!this.pointerLocked) (this.canvas.requestPointerLock?.() as Promise<void> | undefined)?.catch?.(() => {});
  }

  releaseLock(): void {
    if (this.pointerLocked) document.exitPointerLock?.();
  }

  /** Call at the end of each frame. */
  endFrame(): void {
    this.pressedFrame.clear();
    this.typedFrame.clear();
    this.mouseDX = 0;
    this.mouseDY = 0;
    this.wheel = 0;
    this.hovered = false;
  }
}

function isTyping(e: KeyboardEvent): boolean {
  const t = e.target as HTMLElement | null;
  return !!t && (t.tagName === 'INPUT' || t.tagName === 'SELECT' || t.tagName === 'TEXTAREA');
}
